import { useState, useEffect } from "react";
import axios from "axios";
import { Building2, Tag, Plus, Trash2, Loader2, Search, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MaintenancePage() {
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingManufacturers, setLoadingManufacturers] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  
  // New category dialog
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  
  // New manufacturer dialog
  const [showNewManufacturerDialog, setShowNewManufacturerDialog] = useState(false);
  const [newManufacturerName, setNewManufacturerName] = useState("");
  const [savingManufacturer, setSavingManufacturer] = useState(false);
  
  // New location dialog
  const [showNewLocationDialog, setShowNewLocationDialog] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  
  // Search
  const [categorySearch, setCategorySearch] = useState("");
  const [manufacturerSearch, setManufacturerSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");

  useEffect(() => {
    fetchCategories();
    fetchManufacturers();
    fetchLocations();
  }, []);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Kunde inte ladda kategorier");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchManufacturers = async () => {
    setLoadingManufacturers(true);
    try {
      const response = await axios.get(`${API}/manufacturers`);
      setManufacturers(response.data);
    } catch (error) {
      console.error("Error fetching manufacturers:", error);
      toast.error("Kunde inte ladda tillverkare");
    } finally {
      setLoadingManufacturers(false);
    }
  };

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Kunde inte ladda lagerplatser");
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Ange ett kategorinamn");
      return;
    }
    
    setSavingCategory(true);
    try {
      await axios.post(`${API}/categories`, { name: newCategoryName.trim() });
      toast.success(`Kategori "${newCategoryName}" skapad`);
      setNewCategoryName("");
      setShowNewCategoryDialog(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa kategori");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (name) => {
    if (!confirm(`Är du säker på att du vill ta bort kategorin "${name}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/categories/${encodeURIComponent(name)}`);
      toast.success(`Kategori "${name}" borttagen`);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort kategori");
    }
  };

  const handleAddManufacturer = async () => {
    if (!newManufacturerName.trim()) {
      toast.error("Ange ett tillverkarnamn");
      return;
    }
    
    setSavingManufacturer(true);
    try {
      await axios.post(`${API}/manufacturers`, { name: newManufacturerName.trim() });
      toast.success(`Tillverkare "${newManufacturerName}" skapad`);
      setNewManufacturerName("");
      setShowNewManufacturerDialog(false);
      fetchManufacturers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa tillverkare");
    } finally {
      setSavingManufacturer(false);
    }
  };

  const handleDeleteManufacturer = async (name) => {
    if (!confirm(`Är du säker på att du vill ta bort tillverkaren "${name}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/manufacturers/${encodeURIComponent(name)}`);
      toast.success(`Tillverkare "${name}" borttagen`);
      fetchManufacturers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort tillverkare");
    }
  };

  const handleAddLocation = async () => {
    if (!newLocationName.trim()) {
      toast.error("Ange ett lagerplatsnamn");
      return;
    }
    
    setSavingLocation(true);
    try {
      await axios.post(`${API}/locations`, { name: newLocationName.trim() });
      toast.success(`Lagerplats "${newLocationName.toUpperCase()}" skapad`);
      setNewLocationName("");
      setShowNewLocationDialog(false);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa lagerplats");
    } finally {
      setSavingLocation(false);
    }
  };

  const handleDeleteLocation = async (name) => {
    if (!confirm(`Är du säker på att du vill ta bort lagerplatsen "${name}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/locations/${encodeURIComponent(name)}`);
      toast.success(`Lagerplats "${name}" borttagen`);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort lagerplats");
    }
  };

  const filteredCategories = categories.filter(c => 
    c.toLowerCase().includes(categorySearch.toLowerCase())
  );
  
  const filteredManufacturers = manufacturers.filter(m => 
    m.toLowerCase().includes(manufacturerSearch.toLowerCase())
  );

  const filteredLocations = locations.filter(l => 
    l.toLowerCase().includes(locationSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Tag className="h-6 w-6 text-primary" />
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="maintenance-title">
              Kategorier & Tillverkare
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Hantera kategorier, tillverkare och lagerplatser
          </p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Categories Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="h-5 w-5 text-primary" />
                  <CardTitle>Kategorier</CardTitle>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => setShowNewCategoryDialog(true)}
                  className="gap-1"
                  data-testid="add-category-btn"
                >
                  <Plus className="h-4 w-4" />
                  Ny
                </Button>
              </div>
              <CardDescription>
                {categories.length} kategorier
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    placeholder="Sök kategori..."
                    className="pl-10"
                  />
                </div>
                
                {loadingCategories ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Laddar...
                  </div>
                ) : filteredCategories.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {categorySearch ? "Inga kategorier hittades" : "Inga kategorier ännu"}
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {filteredCategories.map((category) => (
                      <div 
                        key={category} 
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 group"
                      >
                        <span className="font-medium">{category}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                          onClick={() => handleDeleteCategory(category)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Manufacturers Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  <CardTitle>Tillverkare</CardTitle>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => setShowNewManufacturerDialog(true)}
                  className="gap-1"
                  data-testid="add-manufacturer-btn"
                >
                  <Plus className="h-4 w-4" />
                  Ny
                </Button>
              </div>
              <CardDescription>
                {manufacturers.length} tillverkare
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={manufacturerSearch}
                    onChange={(e) => setManufacturerSearch(e.target.value)}
                    placeholder="Sök tillverkare..."
                    className="pl-10"
                  />
                </div>
                
                {loadingManufacturers ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Laddar...
                  </div>
                ) : filteredManufacturers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {manufacturerSearch ? "Inga tillverkare hittades" : "Inga tillverkare ännu"}
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {filteredManufacturers.map((manufacturer) => (
                      <div 
                        key={manufacturer} 
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 group"
                      >
                        <span className="font-medium">{manufacturer}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                          onClick={() => handleDeleteManufacturer(manufacturer)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Locations Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <CardTitle>Lagerplatser</CardTitle>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => setShowNewLocationDialog(true)}
                  className="gap-1"
                  data-testid="add-location-btn"
                >
                  <Plus className="h-4 w-4" />
                  Ny
                </Button>
              </div>
              <CardDescription>
                {locations.length} lagerplatser
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    placeholder="Sök lagerplats..."
                    className="pl-10"
                  />
                </div>
                
                {loadingLocations ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Laddar...
                  </div>
                ) : filteredLocations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {locationSearch ? "Inga lagerplatser hittades" : "Inga lagerplatser ännu"}
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {filteredLocations.map((location) => (
                      <div 
                        key={location} 
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 group"
                      >
                        <span className="font-mono font-medium">{location}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                          onClick={() => handleDeleteLocation(location)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* New Category Dialog */}
      <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-primary" />
              Ny kategori
            </DialogTitle>
            <DialogDescription>
              Lägg till en ny produktkategori
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Kategorinamn</Label>
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="T.ex. Induktionshäll"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCategoryDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handleAddCategory} disabled={savingCategory}>
              {savingCategory ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Lägg till
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Manufacturer Dialog */}
      <Dialog open={showNewManufacturerDialog} onOpenChange={setShowNewManufacturerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Ny tillverkare
            </DialogTitle>
            <DialogDescription>
              Lägg till en ny tillverkare
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Tillverkarnamn</Label>
            <Input
              value={newManufacturerName}
              onChange={(e) => setNewManufacturerName(e.target.value)}
              placeholder="T.ex. Bosch"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleAddManufacturer()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewManufacturerDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handleAddManufacturer} disabled={savingManufacturer}>
              {savingManufacturer ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Lägg till
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Location Dialog */}
      <Dialog open={showNewLocationDialog} onOpenChange={setShowNewLocationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Ny lagerplats
            </DialogTitle>
            <DialogDescription>
              Lägg till en ny lagerplats (omvandlas till versaler automatiskt)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Lagerplatsnamn</Label>
            <Input
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value.toUpperCase())}
              placeholder="T.ex. A1, UTE-03, HYLLA-B4"
              className="font-mono"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleAddLocation()}
              data-testid="new-location-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLocationDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handleAddLocation} disabled={savingLocation} data-testid="save-location-btn">
              {savingLocation ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Lägg till
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
